import { useEffect, useState } from "react";

/** Ab so vielen Pixeln „fehlender" Höhe gilt die Bildschirmtastatur als offen.
 *  Die einziehbare Safari-Adressleiste macht weniger aus (~80px). */
const TASTATUR_SCHWELLE_PX = 150;

/** Sichtbare Fläche über der Bildschirmtastatur (Visual Viewport). */
export interface SichtbareFlaeche {
  top: number;
  height: number;
}

/**
 * Misst, ob die Bildschirmtastatur offen ist, und liefert dann die sichtbare
 * Fläche darüber.
 *
 * Hintergrund: iOS verkleinert bei offener Tastatur NICHT das Layout. `100dvh`
 * bleibt gleich hoch, die Tastatur legt sich nur darüber, und Safari schiebt
 * die ganze Seite nach oben, damit das Eingabefeld sichtbar wird. Dann
 * verschwindet die Kopfleiste oben aus dem Bild. Nur `window.visualViewport`
 * kennt die tatsächlich sichtbare Fläche (`height`) und wie weit iOS sie
 * verschoben hat (`offsetTop`). Wer die Shell an diese Werte hängt, behält die
 * Kopfleiste oben und scrollt nur den Inhalt.
 *
 * `null` = Tastatur zu (oder kein Visual Viewport / Hook inaktiv).
 */
export function useBildschirmTastatur(aktiv: boolean): SichtbareFlaeche | null {
  const [flaeche, setFlaeche] = useState<SichtbareFlaeche | null>(null);

  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!aktiv || !vv) {
      setFlaeche(null);
      return;
    }
    let warOffen = false;
    const messen = () => {
      // innerHeight = Layout-Viewport, schrumpft auf iOS mit der Tastatur nicht.
      // Pinch-Zoom verkleinert vv.height ebenfalls — dann (scale > 1) nichts tun.
      const offen = vv.scale <= 1.01 && window.innerHeight - vv.height > TASTATUR_SCHWELLE_PX;
      if (!offen) {
        warOffen = false;
        setFlaeche((alt) => (alt ? null : alt));
        return;
      }
      const top = Math.round(vv.offsetTop);
      const height = Math.round(vv.height);
      setFlaeche((alt) => (alt && alt.top === top && alt.height === height ? alt : { top, height }));
      if (!warOffen) {
        warOffen = true;
        // Nach dem Verkleinern der Shell das Feld INNERHALB des Inhalts
        // sichtbar holen — sonst liegt es jetzt unter dem neuen unteren Rand.
        requestAnimationFrame(() => {
          const el = document.activeElement;
          if (el instanceof HTMLElement && el !== document.body) {
            el.scrollIntoView({ block: "nearest" });
          }
        });
      }
    };
    messen();
    vv.addEventListener("resize", messen);
    vv.addEventListener("scroll", messen);
    return () => {
      vv.removeEventListener("resize", messen);
      vv.removeEventListener("scroll", messen);
    };
  }, [aktiv]);

  return flaeche;
}
